import { Chip, ChipProps } from "../../../../components/Chip";
import { Flex } from "../../../../components/Flex";
import { TableEmptyCellContent } from "../base/cells/TableEmptyCellContent";
import { TruncateChips } from "../internal/TruncateChips";

/**
 * The accepted value type for the chips formatter function
 */
export type ChipsValueType = string | string[] | ChipProps | ChipProps[];

/**
 * Props for the chips formatter function
 */
export type ChipsFormatterOptions = {
  /**
   * Whether to truncate the chips instead of wrapping
   */
  truncateChips?: boolean;
};

/**
 * Helper function for formatting table cells with chips
 * @param value - The value to format
 * @param options - The options for the chips formatter
 * @returns The formatted chips
 */
export const chipsFormatter = (
  value?: ChipsValueType,
  { truncateChips }: ChipsFormatterOptions = { truncateChips: false },
) => {
  if (!value) return <TableEmptyCellContent />;

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return <TableEmptyCellContent />;
    }

    const isStringArray = value.every((v) => typeof v === "string");
    const items = isStringArray
      ? value.map((v) => ({ label: v }))
      : (value as ChipProps[]);

    if (truncateChips) {
      return <TruncateChips items={items} />;
    }

    return (
      <Flex direction="row" gap="2" wrap="wrap">
        {items.map((item, index) => (
          <Chip key={`${item.label}-${index}`} {...item} />
        ))}
      </Flex>
    );
  }

  if (typeof value === "string") {
    return <Chip label={value} />;
  }

  return <Chip {...value} />;
};
